import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { Select, Tooltip } from 'antd'
import { ArrowUpRight } from 'lucide-react'
import { Button, Modal } from '@surecompanies/core_components'
import { ColDef, ICellRendererParams } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { getNoyoSyncEvent } from 'enrollment-platform/api/partner-benefit-graph/client'
import { RelationshipIcon } from 'modules/annual-benefit-planning/modules/shared/presentation/components/RelationshipIcon'
import {
  ANCILLARY_EXTRA_LINE_LABEL,
  AncillaryExtraLine,
  AncillaryLineCell,
  AncillaryReportRow,
} from '../api/ancillary-report.types'
import guardianFavicon from '../assets/guardian-favicon.svg?url'
import surecoFavicon from '../assets/sureco-favicon.svg?url'
import {
  employeeCoveragePath,
  noyoEmployeeUrl,
} from '../utils/ancillary-report-links'
import {
  FamilyKey,
  FamilyRow,
  useAncillaryFamilies,
} from '../hooks/useAncillaryFamilies'
import { formatRange, lineCellStyle } from '../utils/ancillary-report-cells'

const GridContainer = styled.div`
  background-color: #ffffff;
  border: 1px solid #d6d8d9;
  border-radius: 4px;

  .ag-cell {
    line-height: 20px;
    padding-top: 6px;
    padding-bottom: 6px;
    white-space: normal;
  }

  /*
   * Coverage ranges stay on one line. ag-grid's wrap class uses
   * word-break: break-all, which would split a date mid-string.
   * Status and next action wrap on spaces so the full sentence is visible.
   */
  .ag-cell.ancillary-nowrap,
  .ag-cell.ancillary-nowrap .ag-cell-value {
    white-space: nowrap;
    word-break: normal;
  }

  .ag-cell.ancillary-full,
  .ag-cell.ancillary-full .ag-cell-value {
    white-space: pre-wrap;
    word-break: normal;
    overflow-wrap: break-word;
  }
`

const CoverageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const CoverageLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`

const SourceMark = styled.img`
  width: 20px;
  height: 20px;
  display: block;
`

const RELATIONSHIP_LABEL: Record<AncillaryReportRow['relationship'], string> = {
  employee: 'Employee',
  spouse: 'Spouse',
  child: 'Child',
}

const EMPLOYMENT_STATUS_LABEL: Record<string, string> = {
  'full-time': 'Full Time',
  'part-time': 'Part Time',
}

const NameBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const NameText = styled.div`
  min-width: 0;
`

const NameLink = styled.a`
  color: inherit;
  font-weight: 600;
`

const NameCaption = styled.div`
  color: #5c6570;
  font-size: 12px;
`

const ProblemCaption = styled(NameCaption)`
  color: #c00000;
`

const NoyoLink = styled.a`
  && {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-top: 2px;
    color: #8597b0;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: normal;
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
  }
`

const NoyoWord = styled.span`
  color: #8597b0;
  font-size: 12px;
  font-weight: 400;
  text-decoration: underline;
  text-underline-offset: 3px;
`

const FamilyChevron = styled.button<{ $open: boolean }>`
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: grid;
  place-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  color: #5c6570;
  transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'none')};
`

const ChevronSlot = styled.div`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  flex-shrink: 0;
`

const MemberGuide = styled.div`
  width: 16px;
  align-self: stretch;
  border-left: 1px solid #c5ccd3;
  flex-shrink: 0;
`

function employmentStatusLabel(status: string | null): string | null {
  const trimmed = status?.trim() ?? ''
  if (trimmed.length === 0) {
    return null
  }
  return EMPLOYMENT_STATUS_LABEL[trimmed.toLowerCase()] ?? trimmed
}

export function personCaption(
  relationship: AncillaryReportRow['relationship'],
  employmentStatus: string | null
): string {
  const relationshipLabel = RELATIONSHIP_LABEL[relationship]
  if (relationship !== 'employee') {
    return relationshipLabel
  }
  const statusLabel = employmentStatusLabel(employmentStatus)
  if (statusLabel == null) {
    return relationshipLabel
  }
  return `${relationshipLabel} - ${statusLabel}`
}

function SureCoIcon() {
  return (
    <Tooltip title="SureCo">
      <span>
        <SourceMark src={surecoFavicon} alt="SureCo" />
      </span>
    </Tooltip>
  )
}

function GuardianIcon() {
  return (
    <Tooltip title="Guardian">
      <span>
        <SourceMark src={guardianFavicon} alt="Guardian" />
      </span>
    </Tooltip>
  )
}

function LineCellRenderer({
  value,
}: ICellRendererParams<AncillaryReportRow, AncillaryLineCell | null>) {
  if (!value) return null
  return (
    <CoverageStack>
      <CoverageLine>
        <SureCoIcon />
        <span>{formatRange(value.sureco)}</span>
      </CoverageLine>
      <CoverageLine>
        <GuardianIcon />
        <span>{formatRange(value.noyo)}</span>
      </CoverageLine>
    </CoverageStack>
  )
}

function NameCellRenderer({
  data,
  setExpanded,
}: ICellRendererParams<FamilyRow, string> & {
  setExpanded: (key: FamilyKey, open: boolean) => void
}) {
  if (!data) return null
  const noyoId = data.noyoEmployeeId
  const fullName = `${data.firstName} ${data.lastName}`.trim()
  const family = data.family
  const nested = family.recordType === 'single' && family.key != null
  return (
    <NameBlock>
      <ChevronSlot>
        {family.recordType === 'group' ? (
          <FamilyChevron
            type="button"
            $open={family.expanded}
            aria-expanded={family.expanded}
            aria-label={family.expanded ? 'Collapse family' : 'Expand family'}
            onClick={() => setExpanded(family.key, !family.expanded)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="m9 6 6 6-6 6" />
            </svg>
          </FamilyChevron>
        ) : null}
      </ChevronSlot>
      {nested ? <MemberGuide aria-hidden /> : null}
      <RelationshipIcon relationship={data.relationship} size="sm" />
      <NameText>
        <NameLink
          href={employeeCoveragePath(data.employeePublicKey)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {fullName}
        </NameLink>
        {data.relationship === 'employee' ? (
          <NameCaption>
            {personCaption(data.relationship, data.employmentStatus)}
          </NameCaption>
        ) : null}
        {family.recordType === 'group' && !family.expanded
          ? family.folded.map((dependent) => {
              const Caption = dependent.problem ? ProblemCaption : NameCaption
              return (
                <Caption key={dependent.id}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <RelationshipIcon
                      relationship={dependent.relationship}
                      size="sm"
                    />
                    <span>{dependent.name}</span>
                  </span>
                </Caption>
              )
            })
          : null}
      </NameText>
      {noyoId == null ? null : (
        <NoyoLink
          href={noyoEmployeeUrl(noyoId)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${fullName} in Guardian`}
        >
          <NoyoWord>Noyo</NoyoWord>
          <ArrowUpRight size={12} color="#8597b0" aria-hidden />
        </NoyoLink>
      )}
    </NameBlock>
  )
}

function lineColumnStyle(cell: AncillaryLineCell | null | undefined) {
  return {
    whiteSpace: 'nowrap' as const,
    ...(lineCellStyle(cell) ?? {}),
  }
}

function lineColumn(
  field: 'dental' | 'vision',
  headerName: string
): ColDef<FamilyRow> {
  return {
    field,
    headerName,
    minWidth: 300,
    sortable: false,
    wrapText: false,
    cellClass: 'ancillary-nowrap',
    cellRenderer: LineCellRenderer,
    cellStyle: (params) => lineColumnStyle(params.value),
  }
}

function extraLineColumn(line: AncillaryExtraLine): ColDef<FamilyRow> {
  return {
    colId: `line-${line}`,
    headerName: ANCILLARY_EXTRA_LINE_LABEL[line],
    minWidth: 300,
    sortable: false,
    wrapText: false,
    cellClass: 'ancillary-nowrap',
    valueGetter: (params) => params.data?.otherLines[line] ?? null,
    cellRenderer: LineCellRenderer,
    cellStyle: (params) => lineColumnStyle(params.value),
  }
}

const UNASSIGNED = '__no_one__'

type AncillaryReportGridProps = {
  rows: AncillaryReportRow[]
  extraLines: AncillaryExtraLine[]
  isPlatformAdmin?: boolean
  assigneeOptions?: string[]
  isAssigning?: boolean
  onAssign?: (input: { benefitIds: string[]; assignee: string | null }) => void
  onSync?: (input: {
    employeePublicKey: string
    employmentStatus: string | null
  }) => void
  syncingEmployeePublicKeys?: ReadonlySet<string>
}

function SyncCellRenderer({
  data,
  onSync,
  syncingEmployeePublicKeys,
}: ICellRendererParams<FamilyRow, undefined> & {
  onSync?: AncillaryReportGridProps['onSync']
  syncingEmployeePublicKeys?: ReadonlySet<string>
}) {
  if (!data) return null
  const nested = data.family.recordType === 'single' && data.family.key != null
  if (nested) return null
  const fullName = `${data.firstName} ${data.lastName}`.trim()
  const terminated =
    getNoyoSyncEvent(data.employmentStatus) === 'EMPLOYEE_TERMINATED'
  const syncing =
    syncingEmployeePublicKeys?.has(data.employeePublicKey) ?? false
  return (
    <Button
      type="primary"
      size="small"
      danger={terminated}
      loading={syncing}
      disabled={syncing}
      onClick={() => {
        Modal.confirm({
          title: 'Sync to Noyo?',
          content: terminated
            ? `This sends a termination sync for ${fullName}.`
            : `This sends a cart commit sync for ${fullName}.`,
          okText: terminated ? 'Sync termination' : 'Sync cart commit',
          cancelText: 'Cancel',
          ...(terminated ? { okButtonProps: { danger: true } } : {}),
          onOk: () =>
            onSync?.({
              employeePublicKey: data.employeePublicKey,
              employmentStatus: data.employmentStatus,
            }),
        })
      }}
    >
      {terminated ? 'Sync To Noyo (termination)' : 'Sync To Noyo (cart commit)'}
    </Button>
  )
}

function AssignCellRenderer({
  data,
  assigneeOptions,
  isAssigning,
  onAssign,
}: ICellRendererParams<FamilyRow, undefined> & {
  assigneeOptions: string[]
  isAssigning: boolean
  onAssign?: AncillaryReportGridProps['onAssign']
}) {
  if (!data) return null
  const names = Array.from(
    new Set([...(data.assignee ? [data.assignee] : []), ...assigneeOptions])
  )
  const hasBenefit = data.benefitIds.length > 0
  const select = (
    <Select
      aria-label={`Assign to ${data.firstName} ${data.lastName}`}
      size="small"
      showSearch
      style={{ width: '100%' }}
      disabled={!hasBenefit || isAssigning}
      value={data.assignee ?? UNASSIGNED}
      options={[
        { value: UNASSIGNED, label: 'no one' },
        ...names.map((name) => ({ value: name, label: name })),
      ]}
      onChange={(value: string) => {
        onAssign?.({
          benefitIds: data.benefitIds,
          assignee: value === UNASSIGNED ? null : value,
        })
      }}
    />
  )
  if (hasBenefit) return select
  return (
    <Tooltip title="No Sureco benefit to assign">
      <span>{select}</span>
    </Tooltip>
  )
}

export function AncillaryReportGrid({
  rows,
  extraLines,
  isPlatformAdmin = false,
  assigneeOptions = [],
  isAssigning = false,
  onAssign,
  onSync,
  syncingEmployeePublicKeys,
}: AncillaryReportGridProps) {
  const { grid, setExpanded } = useAncillaryFamilies(rows)
  const gridRef = useRef<AgGridReact<FamilyRow>>(null)
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ columns: ['sync'], force: true })
  }, [syncingEmployeePublicKeys])
  const columnDefs = useMemo<ColDef<FamilyRow>[]>(
    () => [
      {
        colId: 'name',
        headerName: 'Name',
        minWidth: 260,
        wrapText: true,
        cellClass: 'ancillary-full',
        valueGetter: (params) =>
          `${params.data?.firstName ?? ''} ${params.data?.lastName ?? ''}`.trim(),
        cellRenderer: NameCellRenderer,
        cellRendererParams: { setExpanded },
        // The chevron reads family.expanded, not the name string. AG Grid
        // redraws a cell only when its value changes, so a second click kept
        // the closed chevron and asked to expand again.
        equals: () => false,
      },
      lineColumn('dental', 'Dental'),
      lineColumn('vision', 'Vision'),
      ...extraLines.map(extraLineColumn),
      {
        field: 'noyoStatus',
        headerName: 'What Guardian thinks',
        minWidth: 280,
        wrapText: true,
        cellClass: 'ancillary-full',
      },
      ...(isPlatformAdmin
        ? [
            {
              field: 'nextAction',
              headerName: 'Next action',
              minWidth: 280,
              wrapText: true,
              cellClass: 'ancillary-full',
            } satisfies ColDef<FamilyRow>,
            {
              colId: 'sync',
              headerName: 'Sync',
              minWidth: 240,
              sortable: false,
              cellRenderer: SyncCellRenderer,
              cellRendererParams: {
                onSync,
                syncingEmployeePublicKeys,
              },
            } satisfies ColDef<FamilyRow>,
            {
              colId: 'assign',
              headerName: 'Assign to',
              minWidth: 200,
              sortable: false,
              cellRenderer: AssignCellRenderer,
              cellRendererParams: {
                assigneeOptions,
                isAssigning,
                onAssign,
              },
            } satisfies ColDef<FamilyRow>,
          ]
        : []),
    ],
    [
      extraLines,
      assigneeOptions,
      isAssigning,
      isPlatformAdmin,
      onAssign,
      onSync,
      setExpanded,
      syncingEmployeePublicKeys,
    ]
  )

  const defaultColDef = useMemo<ColDef<FamilyRow>>(
    () => ({ resizable: true, sortable: true, flex: 1, autoHeight: true }),
    []
  )

  return (
    <GridContainer className="ag-theme-alpine">
      <AgGridReact<FamilyRow>
        ref={gridRef}
        {...grid}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        overlayNoRowsTemplate="No people on this tab"
      />
    </GridContainer>
  )
}
